import { Grid, GridItem, useIsWindowWidthGreaterThan } from '@outdoorsyco/bonfire';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import MaxWidth from '@/components/common/layout/MaxWidth';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';
import { useRouter } from '@/hooks/useRouter';
import { getAuthenticatedUser } from '@/redux/selectors/auth/user';
import { useUserData } from '@/services/getUserData';

import { EAccountPageTab, Navigation } from '../Navigation/Navigation';
import { Notifications } from '../Tabs/Notifications/Notifications';
import { Payment } from '../Tabs/Payment/Payment';
import { Profile } from '../Tabs/Profile/Profile';
import { ProfileEdit } from '../Tabs/ProfileEdit/ProfileEdit';

interface IAcountDetailsProps {
  defaultSelectedTab?: EAccountPageTab;
}

export const Details = ({ defaultSelectedTab = EAccountPageTab.Profile }: IAcountDetailsProps) => {
  const router = useRouter();
  const { locale } = useLocaleSettings();
  const desktopView = useIsWindowWidthGreaterThan('sm', true);

  const authenticatedUser = useSelector(getAuthenticatedUser);

  const [selectedAccountTab, setSelectedAccountTab] = useState<EAccountPageTab>(defaultSelectedTab);

  const {
    data: userData,
    mutate: refetchUser,
    isLoading: isLoadingUser,
  } = useUserData(authenticatedUser?.id);
  const fullUser = userData?.data;

  const stripeKey =
    (fullUser?.profile.address.country &&
      process.env.stripeCountries[fullUser?.profile.address.country]) ||
    process.env.stripePublishableKey;
  const stripePromise = useMemo(() => (stripeKey ? loadStripe(stripeKey) : null), [stripeKey]);

  useEffect(() => {
    if (router.query.tab) {
      setSelectedAccountTab(router.query.tab as EAccountPageTab);
    }
  }, [router.query]);

  useEffect(() => {
    if (router.query && router.query['zone'] && router.query['zone'] === 'Password') {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountTab]);

  useEffect(() => {
    const result = Object.keys(EAccountPageTab).find(
      key =>
        EAccountPageTab[key as keyof typeof EAccountPageTab] === router.query['tab']?.toString(),
    );

    if (result) {
      return setSelectedAccountTab(EAccountPageTab[result as keyof typeof EAccountPageTab]);
    } else {
      const { pathname, query } = router;
      delete query.tab;
      router.push(
        {
          pathname,
          query,
        },
        undefined,
        { shallow: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!fullUser || !stripePromise) {
    return null;
  }

  return (
    // @ts-expect-error This is a type issue with our and their locale. StripeElementLocale !== TLocale
    <Elements options={{ locale: locale }} stripe={stripePromise}>
      <MaxWidth className="mx-auto">
        <Navigation selectedTab={selectedAccountTab} setSelectedTab={setSelectedAccountTab} />
        <Grid columnSpacing={desktopView ? 0 : 2}>
          {desktopView && <GridItem sm={1} xs={1} />}
          <GridItem sm={12} xs={desktopView ? 10 : 12} className="px-4 pt-3 md:px-0 md:pt-5">
            {selectedAccountTab === EAccountPageTab.Profile && (
              <Profile
                userData={fullUser}
                refetchUser={refetchUser}
                isLoadingUser={isLoadingUser}
              />
            )}
            {selectedAccountTab === EAccountPageTab.ProfileEdit && (
              <ProfileEdit userData={fullUser} refetchUser={refetchUser} />
            )}
            {selectedAccountTab === EAccountPageTab.PaymentMethods && fullUser && (
              <Payment userData={fullUser} />
            )}
            {selectedAccountTab === EAccountPageTab.Notifications && fullUser && (
              <Notifications userData={fullUser} refreshUser={refetchUser} />
            )}
          </GridItem>
          {desktopView && <GridItem sm={1} xs={1} />}
        </Grid>
      </MaxWidth>
    </Elements>
  );
};
